import Txp from './Txp';
import TxpLoadingAndErrorCard from './TxpLoadingAndErrorCard';
import UserVoice from './UserVoice';
import { MAIL_LOCATION } from '../helpers/TxpConstants';
import LoadingEndState from '../store/schema/LoadingEndState';
import TxpDataPointNames from '../store/schema/telemetry/TxpDataPointNames';
import OwaTxpStyle from '../store/viewSchema/OwaTxpStyle';
import getSaveEditableRequestBody, {
    EditableRequestBody,
} from '../utils/actions/getSaveEditableRequestBody';
import shouldShowEditButton from '../utils/view/shouldShowEditButton';
import onAddToCalendarClickHandler from '../utils/actions/onAddToCalendarClickHandler';
import onRemoveFromCalendarClickHandler from '../utils/actions/onRemoveFromCalendarClickHandler';
import onReportIssueHandler from '../utils/actions/onReportIssueHandler';
import onSaveEditableCardClickHandler from '../utils/actions/onSaveEditableCardClickHandler';
import { areAnyTXPEntitiesSupported } from 'owa-txp-common';
import isEditableCardSupported from '../utils/featureChecks/isEditableCardSupported';
import endLoadingPerfDatapointHandler from '../utils/telemetry/endLoadingPerfDatapointHandler';
import populateCardViewedDatapoint from '../utils/telemetry/populateCardViewedDatapoint';
import logCounterFactualDatapoint from '../utils/telemetry/logCounterFactualDatapoint';
import createMailTxpConfiguration from '../utils/view/createMailTxpConfiguration';
import createTxpStrings from '../utils/view/createTxpStrings';
import { createMailCardStyle } from '../utils/view/createTxpStyle';
import getInternetMessageId from '../utils/view/getInternetMessageId';
import getOriginFromTxpObject from '../utils/view/getOriginFromTxpObject';
import { populateMailImages } from '../utils/view/populateImages';
import shouldHideTxpPanel from '../utils/view/shouldHideTxpPanel';
import { observer } from 'mobx-react';
import { getPackageBaseUrl } from 'owa-config';
import OwaCoreTxpStrings from 'owa-txp/lib/store/schema/OwaCoreTxpStrings';
import { getCalendarEventPeekDeepLinkUrl } from 'owa-url';
import * as React from 'react';
import { TxpDatapoint, TxpObject } from 'txp-data';
import TxpConfiguration from 'txp-data/lib/schema/configSchema/TxpConfiguration';
import TxpStatus from 'txp-data/lib/schema/TxpStatus';
import CardItem from 'txp-data/lib/schema/viewSchema/CardItem';
import convertTxpBlobToObject from 'txp-data/lib/utils/data/convertTxpBlobToObject';
import convertTxpStatusBlobToObject from 'txp-data/lib/utils/data/convertTxpStatusBlobToObject';
import GWSRequestErrorInfo from '../store/schema/GWSRequestErrorInfo';
import TxpEntitiesState from 'txp-data/lib/schema/innerSchema/TxpEntitiesState';
import handleSaveEditableResponse from '../utils/telemetry/handleSaveEditableResponse';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import getAriaText from '../utils/view/getAriaText';
import { isFeatureEnabled } from 'owa-feature-flags';
import * as ReactDOM from 'react-dom';
import TxpSettingsDialog from './TxpSettingsDialog';
import PostalAddress from 'txp-data/lib/schema/innerSchema/PostalAddress';
import EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';
import { createEnhancedLocationFromTxpData } from 'owa-location-persona';
import { getLocalizedAddress } from 'owa-location-display-text';
import getRequestBodyFields from '../utils/view/getRequestBodyFields';
import getTxpSettings from 'owa-calendar-configuration/lib/utils/getTxpSettings';
import { TxpDisableReason, reloadTxpSettings } from 'owa-calendar-configuration';
import { trace } from 'owa-trace';
import updateLocalTxpSettings from 'owa-txp-option/lib/utils/updateLocalTxpSettings';
import { IsShadowMailboxUser } from 'owa-mail-ads-shared/lib/sharedAdsUtils';

let styles = require('./Txp.scss');

export interface TxpWrapperState {
    txpObject: TxpObject;
    txpStatusObject: TxpStatus;
}

export interface TxpWrapperProps extends React.HTMLProps<HTMLDivElement> {
    entityNamesMap: number;
    txpBlob: string;
    txpStatusBlob: string;
    owaTheme?: string;
    isDarkTheme?: boolean;
    ItemId?: string;
    InternetMessageId?: string;
    calendarEventIds?: any[];

    // Callback hooks for logging data points
    datapointLogger?: (
        txpDatapoint: TxpDatapoint,
        datapointName: string,
        internetMessageId?: string
    ) => void;

    getPerfDatapoint?: (datapointName: string) => PerformanceDatapoint;

    //Callback hook for Events from email settings card action
    onEventsFromEmailSettingsClicked?: () => void;

    //Supported feature flags
    isInvoiceSupported?: boolean;
    isServiceReservationSupported?: boolean;
}

@observer
export default class TxpWrapper extends React.Component<TxpWrapperProps, TxpWrapperState> {
    private txpConfiguration: TxpConfiguration;
    private loadingPerfDatapoint: PerformanceDatapoint;
    private isLoading: boolean;
    private txpStrings: OwaCoreTxpStrings;

    constructor(props: TxpWrapperProps) {
        super(props);
        const {
            entityNamesMap,
            txpBlob,
            txpStatusBlob,
            isInvoiceSupported,
            isServiceReservationSupported,
        } = this.props;

        // Setting up the TxpConfiguration
        this.txpConfiguration = createMailTxpConfiguration(
            isEditableCardSupported(),
            isInvoiceSupported,
            entityNamesMap,
            isServiceReservationSupported,
            this.onReportIssue,
            this.onEventsFromEmailSettingsClicked,
            this.onAddToCalendarClicked,
            this.onRemoveFromCalendarClicked,
            this.onSaveEditableCard,
            getCalendarEventPeekDeepLinkUrl,
            this.getDatapointLogger,
            this.getPerfDatapointLogger,
            getAriaText,
            this.getDisplayAddress
        );

        this.isLoading = false;
        const txpStatusObject: TxpStatus = convertTxpStatusBlobToObject(txpStatusBlob);
        const shouldShowCard = areAnyTXPEntitiesSupported(entityNamesMap);
        if (shouldShowCard) {
            let txpObject = convertTxpBlobToObject(
                txpBlob,
                this.txpConfiguration.isInvoiceEnabled,
                this.txpConfiguration.isServiceReservationEnabled,
                null /* shouldCreateActivities */,
                null /* useEntityId */,
                this.txpConfiguration.txpDateTimeCallbacks &&
                    this.txpConfiguration.txpDateTimeCallbacks.isValidDate
            );

            txpStatusObject && this.setEditableButtonState(txpStatusObject.state);
            this.state = {
                txpObject: txpObject,
                txpStatusObject: txpStatusObject,
            };
            txpObject && this.onCardViewed();
        }
        logCounterFactualDatapoint(entityNamesMap, txpStatusObject, shouldShowCard);
    }

    UNSAFE_componentWillReceiveProps(newProps: TxpWrapperProps) {
        if (this.props.txpBlob != newProps.txpBlob) {
            let txpObject = convertTxpBlobToObject(
                newProps.txpBlob,
                this.txpConfiguration.isInvoiceEnabled,
                this.txpConfiguration.isServiceReservationEnabled
            );
            let txpStatusObject = convertTxpStatusBlobToObject(newProps.txpStatusBlob);
            txpStatusObject && this.setEditableButtonState(txpStatusObject.state);
            this.setState({
                txpObject: txpObject,
                txpStatusObject: txpStatusObject,
            });
        }
        if (
            (newProps.owaTheme !== this.props.owaTheme && this.txpConfiguration) ||
            newProps.isDarkTheme !== this.props.isDarkTheme
        ) {
            this.txpConfiguration.txpImages = populateMailImages();
            this.txpConfiguration.txpImages.packageBaseUrl = getPackageBaseUrl;
        }
    }

    componentWillUnmount() {
        this.endDatapointsIfLoading(LoadingEndState.UserNavigatedAway);
    }

    componentDidMount() {
        reloadTxpSettings();
        updateLocalTxpSettings();
    }

    render() {
        const {
            className,
            calendarEventIds,
            entityNamesMap,
            datapointLogger,
            ItemId,
            InternetMessageId,
        } = this.props;

        if (!this.state) {
            return null;
        }

        let { txpObject, txpStatusObject } = this.state;

        this.txpStrings = createTxpStrings();

        const txpStyle: OwaTxpStyle = createMailCardStyle(styles);

        let showPermanentErrorCard: boolean = false;

        if (shouldHideTxpPanel(txpObject, txpStatusObject)) {
            // Since loading UI is being displayed to the user returning null may result in a bad experience.
            // In these cases, show error card to convey that we weren't able to process the data correctly
            if (this.isLoading) {
                showPermanentErrorCard = true;
            } else {
                return null;
            }
        }

        if (txpObject) {
            this.endDatapointsIfLoading(LoadingEndState.Success);
            return (
                <div className={className}>
                    <Txp
                        txpStyle={txpStyle}
                        txpStrings={this.txpStrings}
                        txpObject={txpObject}
                        calendarEventIds={calendarEventIds}
                        txpConfiguration={this.txpConfiguration}
                        className={styles.cardCollection}
                    />
                    <UserVoice
                        entityNamesMap={entityNamesMap}
                        datapointLogger={datapointLogger}
                        feedbackLocationName={MAIL_LOCATION}
                        txpObject={txpObject}
                        txpStatusObject={txpStatusObject}
                        txpConfiguration={this.txpConfiguration}
                        itemId={ItemId}
                        internetMessageId={InternetMessageId}
                    />
                </div>
            );
        } else {
            return (
                <div className={className}>
                    <TxpLoadingAndErrorCard
                        txpStatus={txpStatusObject}
                        entityNamesMap={entityNamesMap}
                        showPermanentErrorCard={showPermanentErrorCard}
                        onHelpUsImprove={this.onReportIssue}
                        datapointLogger={datapointLogger}
                        startLoadingPerfDatapoint={this.startLoadingPerfDatapoint}
                        endLoadingPerfDatapoint={this.endDatapointsIfLoading}
                    />
                </div>
            );
        }
    }

    private onReportIssue = (feedbackType: string, feedbackLocationName: string) => {
        let { datapointLogger, ItemId, InternetMessageId, entityNamesMap } = this.props;
        onReportIssueHandler(
            feedbackType,
            feedbackLocationName,
            datapointLogger,
            this.state.txpObject,
            this.state.txpStatusObject,
            ItemId,
            InternetMessageId,
            entityNamesMap,
            this.txpConfiguration
        );
    };

    private onAddToCalendarClicked = () => {
        onAddToCalendarClickHandler(this.props.ItemId);
    };

    private onRemoveFromCalendarClicked = () => {
        onRemoveFromCalendarClickHandler(this.props.ItemId);
    };

    private onEventsFromEmailSettingsClicked = (cardItem: CardItem) => {
        const settings = getTxpSettings();

        if (isFeatureEnabled('cal-txp-optionsV2') && !IsShadowMailboxUser()) {
            if (settings) {
                settings.TxpDisableReason === TxpDisableReason.None &&
                    this.openEntityLevelSettings(cardItem);
            } else {
                trace.error('[TXP Options V2] TXP Settings undefined in TXPWrapper');
            }
        } else {
            this.props.onEventsFromEmailSettingsClicked();
        }
    };

    private openEntityLevelSettings(cardItem: CardItem) {
        const dialogDiv = document.createElement('div');
        dialogDiv.className = 'TxpSettingsDialogWrapper';
        document.body.appendChild(dialogDiv);
        ReactDOM.render(<TxpSettingsDialog cardItem={cardItem} />, dialogDiv);
    }

    private onSaveEditableCard = async (
        cardItem: CardItem,
        perfDataPoint: PerformanceDatapoint,
        summaryEditableField?: string,
        editableLocation?: PostalAddress,
        onError?: (clientErrorMessage: string, status: DatapointStatus, error?: Error) => void,
        onSuccess?: () => void
    ) => {
        // check if the fields are dirty
        const requestBodyFields = getRequestBodyFields(
            cardItem,
            summaryEditableField,
            editableLocation && editableLocation.displayAddress
        );

        // only when fields are dirty ,generate request body
        let requestBody: EditableRequestBody = getSaveEditableRequestBody(
            cardItem,
            requestBodyFields.summaryEditableField,
            requestBodyFields.detailsEditableField != null && editableLocation
        );

        // No fields were updated
        if (!requestBody) {
            onSuccess();
        } else {
            let gwsRequestErrorInfo: GWSRequestErrorInfo;
            let response = await onSaveEditableCardClickHandler(
                this.props.ItemId,
                requestBody,
                (statusCode: any, serverErrorMessage?: string) => {
                    gwsRequestErrorInfo = { statusCode, serverErrorMessage };
                }
            );

            handleSaveEditableResponse(
                response,
                gwsRequestErrorInfo,
                perfDataPoint,
                onError,
                onSuccess
            );
        }
    };

    private startLoadingPerfDatapoint = () => {
        if (!this.isLoading) {
            this.isLoading = true;
            if (this.props.getPerfDatapoint) {
                this.loadingPerfDatapoint = this.props.getPerfDatapoint(
                    TxpDataPointNames.TXPCardLoading
                );
            }
        }
    };

    private endDatapointsIfLoading = (loadingEndState: LoadingEndState) => {
        if (this.isLoading && this.loadingPerfDatapoint) {
            endLoadingPerfDatapointHandler(
                this.loadingPerfDatapoint,
                this.props.entityNamesMap,
                loadingEndState,
                this.state.txpStatusObject,
                !!this.state.txpObject
            );
            loadingEndState === LoadingEndState.Success && this.onCardViewed();
        }
        this.isLoading = false;
    };

    private onCardViewed = () => {
        const txpObject: TxpObject = this.state ? this.state.txpObject : null;
        const { datapointLogger, entityNamesMap, InternetMessageId } = this.props;
        const origin: string = getOriginFromTxpObject(txpObject);
        const internetMessageId: string = getInternetMessageId(txpObject, InternetMessageId);
        let isEditableCardEnabled = shouldShowEditButton(
            this.txpConfiguration,
            null /* cardItem */,
            entityNamesMap
        );

        populateCardViewedDatapoint(
            datapointLogger,
            origin,
            entityNamesMap,
            internetMessageId,
            isEditableCardEnabled
        );
    };

    private getPerfDatapointLogger = (datapointName: string): PerformanceDatapoint => {
        if (this.props.getPerfDatapoint) {
            return this.props.getPerfDatapoint(datapointName);
        }

        return null;
    };

    private setEditableButtonState = (txpStatusState: TxpEntitiesState) => {
        this.txpConfiguration.isButtonEnabled = txpStatusState != TxpEntitiesState.SaveUserEdits;
    };

    private getDisplayAddress(location: PostalAddress) {
        let txpEnhancedLocation: EnhancedLocation = createEnhancedLocationFromTxpData(location);
        return txpEnhancedLocation && getLocalizedAddress(txpEnhancedLocation.PostalAddress);
    }

    private getDatapointLogger = (
        txpDatapoint: TxpDatapoint,
        datapointName: string,
        internetMessageId?: string
    ) => {
        const loggingInternetMessageId: string = getInternetMessageId(
            this.state.txpObject,
            internetMessageId
        );
        this.props.datapointLogger(txpDatapoint, datapointName, loggingInternetMessageId);
    };
}
